import React from 'react';
import { FONT_MONO } from '../../views/landing/landingData';
import { HvLayer } from './HvLayer';

/**
 * Global toast notifications (design: the landing page's "settle" toast —
 * mono status pill + message, bottom-centre, auto-dismiss).
 *
 *   const toast = useToast();
 *   toast.success('Project deleted.');
 *   toast.error('Could not delete the file.');
 *   toast.show('Payment received.', { tone: 'warn', label: 'PENDING', duration: 8000 });
 *
 * Errors stay a little longer; hovering a toast pauses its timer. At most four
 * toasts are shown at once (oldest drop first).
 */
export type ToastTone = 'success' | 'error' | 'warn' | 'info';

export interface ToastOptions {
    tone?: ToastTone;
    /** Text in the mono pill. Defaults per tone (DONE / FAILED / HEADS UP / NOTE). */
    label?: string;
    /** Milliseconds before auto-dismiss. 0 keeps it until closed. */
    duration?: number;
    /** Optional inline action ("Undo", "View"). Clicking it dismisses the toast. */
    action?: { label: string; onClick: () => void };
}

interface ToastItem extends Required<Pick<ToastOptions, 'tone' | 'label' | 'duration'>> {
    id: string;
    message: React.ReactNode;
    action?: ToastOptions['action'];
}

interface ToastApi {
    show: (message: React.ReactNode, opts?: ToastOptions) => string;
    success: (message: React.ReactNode, opts?: Omit<ToastOptions, 'tone'>) => string;
    error: (message: React.ReactNode, opts?: Omit<ToastOptions, 'tone'>) => string;
    warn: (message: React.ReactNode, opts?: Omit<ToastOptions, 'tone'>) => string;
    info: (message: React.ReactNode, opts?: Omit<ToastOptions, 'tone'>) => string;
    dismiss: (id: string) => void;
}

const DEFAULT_LABEL: Record<ToastTone, string> = { success: 'DONE', error: 'FAILED', warn: 'HEADS UP', info: 'NOTE' };
const DEFAULT_DURATION: Record<ToastTone, number> = { success: 5000, error: 7000, warn: 6000, info: 5000 };
const PILL: Record<ToastTone, { bg: string; fg: string }> = {
    success: { bg: 'var(--hv-mint)', fg: 'var(--hv-mint-ink)' },
    error: { bg: 'var(--hv-danger-bg)', fg: 'var(--hv-danger)' },
    warn: { bg: 'var(--hv-sand)', fg: 'var(--hv-sand-ink)' },
    info: { bg: 'var(--hv-page-2)', fg: 'var(--hv-body)' },
};
const MAX_VISIBLE = 4;

const ToastContext = React.createContext<ToastApi | null>(null);

export const useToast = (): ToastApi => {
    const ctx = React.useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within a ToastProvider');
    return ctx;
};

let seq = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = React.useState<ToastItem[]>([]);

    const dismiss = React.useCallback((id: string) => setItems((prev) => prev.filter((t) => t.id !== id)), []);

    const show = React.useCallback((message: React.ReactNode, opts: ToastOptions = {}) => {
        const tone = opts.tone ?? 'info';
        const id = `toast-${++seq}`;
        const item: ToastItem = {
            id,
            message,
            tone,
            label: opts.label ?? DEFAULT_LABEL[tone],
            duration: opts.duration ?? DEFAULT_DURATION[tone],
            action: opts.action,
        };
        setItems((prev) => [...prev, item].slice(-MAX_VISIBLE));
        return id;
    }, []);

    const api = React.useMemo<ToastApi>(() => ({
        show,
        success: (m, o) => show(m, { ...o, tone: 'success' }),
        error: (m, o) => show(m, { ...o, tone: 'error' }),
        warn: (m, o) => show(m, { ...o, tone: 'warn' }),
        info: (m, o) => show(m, { ...o, tone: 'info' }),
        dismiss,
    }), [show, dismiss]);

    return (
        <ToastContext.Provider value={api}>
            {children}
            {items.length > 0 && (
                <HvLayer>
                    <div aria-live="polite" style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 210, width: 'min(560px, calc(100vw - 32px))', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, pointerEvents: 'none' }}>
                        {items.map((t) => <ToastCard key={t.id} item={t} onClose={() => dismiss(t.id)} />)}
                    </div>
                </HvLayer>
            )}
        </ToastContext.Provider>
    );
};

const ToastCard: React.FC<{ item: ToastItem; onClose: () => void }> = ({ item, onClose }) => {
    const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const start = React.useCallback(() => {
        if (item.duration <= 0) return;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(onClose, item.duration);
    }, [item.duration, onClose]);
    const stop = React.useCallback(() => { if (timer.current) clearTimeout(timer.current); timer.current = null; }, []);
    React.useEffect(() => { start(); return stop; }, [start, stop]);

    const pill = PILL[item.tone];
    return (
        <div
            role={item.tone === 'error' ? 'alert' : 'status'}
            onMouseEnter={stop}
            onMouseLeave={start}
            style={{ pointerEvents: 'auto', maxWidth: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '11px 10px 11px 16px', borderRadius: 14, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 30px 50px -28px var(--hv-shadow)', animation: 'hvPop 0.4s cubic-bezier(0.2,0.9,0.24,1) both' }}
        >
            <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.14em', flex: 'none', height: 24, display: 'flex', alignItems: 'center', padding: '0 9px', borderRadius: 999, background: pill.bg, color: pill.fg }}>{item.label}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--hv-ink)', overflowWrap: 'anywhere' }}>{item.message}</span>
            {item.action && (
                <button type="button" onClick={() => { item.action?.onClick(); onClose(); }} className="hv-h-card2-ink" style={{ flex: 'none', height: 28, padding: '0 12px', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', fontSize: 12.5, fontWeight: 700, color: 'var(--hv-ink)', cursor: 'pointer', fontFamily: 'inherit' }}>{item.action.label}</button>
            )}
            <button type="button" onClick={onClose} aria-label="Dismiss" className="hv-h-ink" style={{ flex: 'none', width: 28, height: 28, borderRadius: 999, border: 0, background: 'transparent', color: 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M7 7l10 10M17 7L7 17" /></svg>
            </button>
        </div>
    );
};
